import React, { useState } from 'react';
import { Collapse, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { EditIcon } from 'src/icons/EditIcon';
import { DeleteIcon } from 'src/icons/DeleteIcon';
import { ParticipantListItem, ParticipantListParticipant, SettingsOption } from 'src/model/api/v2/data-contracts';
import {
    rejectFailedParticipantListSave,
    useAddParticipantToListMutation,
    useDeleteParticipantFromListMutation,
    useDeleteParticipantListMutation,
    useUpdateParticipantInListMutation,
    useUpdateParticipantListMutation,
} from 'src/api/v1/contact-ApiSlice';
import { ParticipantListResponse } from 'src/model/api/contact.model';
import { DeleteUserDialog } from 'src/pages/ProjectUsersPage/components/DeleteUserDialog';
import { ParticipantRow } from 'src/pages/ProfileSettingsPage/components/ParticipantRow';
import { NewParticipantRow } from 'src/pages/ProfileSettingsPage/components/NewParticipantRow';
import { ParticipantListNameInput } from 'src/pages/ProfileSettingsPage/components/ParticipantListNameInput';
import { EMAIL_PATTERN } from 'src/utils/email';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { getV1ErrorMessage } from 'src/utils/v1ErrorMessage';
import { ts } from 'src/utils/translate';

const isSameEmail = (a: string, b: string) => a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

type Props = {
    list: ParticipantListItem;
    roles: SettingsOption[];
    isOpen: boolean;
    isRenaming: boolean;
    isAddingParticipant: boolean;
    isNameTaken: (name: string, exceptListId: number) => boolean;
    onToggle: () => void;
    onStartRename: () => void;
    onStartAddParticipant: () => void;
    onCloseEditor: () => void;
};

export const ParticipantListCard = ({
    list,
    roles,
    isOpen,
    isRenaming,
    isAddingParticipant,
    isNameTaken,
    onToggle,
    onStartRename,
    onStartAddParticipant,
    onCloseEditor,
}: Props) => {
    const dispatch = useAppDispatch();
    const [updateParticipantList, { isLoading: isRenameLoading }] = useUpdateParticipantListMutation();
    const [deleteParticipantList, { isLoading: isDeleteListLoading }] = useDeleteParticipantListMutation();
    const [addParticipantToList, { isLoading: isAddLoading }] = useAddParticipantToListMutation();
    const [updateParticipantInList] = useUpdateParticipantInListMutation();
    const [deleteParticipantFromList, { isLoading: isRemoveLoading }] = useDeleteParticipantFromListMutation();

    const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [removedParticipant, setRemovedParticipant] = useState<ParticipantListParticipant | null>(null);
    const [busyParticipantId, setBusyParticipantId] = useState<number | null>(null);

    const showSuccess = (response: ParticipantListResponse) => {
        dispatch(showSnackBar({ text: response.message, severity: 'success' }));
    };

    const showError = (text: string) => {
        dispatch(showSnackBar({ text, severity: 'error' }));
    };

    const showRequestError = (error: unknown) => {
        showError(getV1ErrorMessage(error, ts('something_went_wrong_try_again', 'Something went wrong, please try again.')));
    };

    const hasParticipant = (email: string, roleId: number) =>
        list.participants.some(participant => isSameEmail(participant.email, email) && participant.role_id === roleId);

    const handleRename = (name: string) => {
        const trimmed = name.trim();
        if (trimmed === '') {
            showError(ts('pl_invalid_name_enter', 'Please enter a name for the participant list.'));
            return;
        }
        if (isNameTaken(trimmed, list.id)) {
            showError(ts('pl_existing_list_name', 'A participant list with that name already exists.'));
            return;
        }
        updateParticipantList({ list_id: list.id, list_name: trimmed })
            .unwrap()
            .then(rejectFailedParticipantListSave)
            .then(response => {
                showSuccess(response);
                onCloseEditor();
            })
            .catch(showRequestError);
    };

    const handleDeleteList = () => {
        deleteParticipantList({ list_id: list.id })
            .unwrap()
            .then(rejectFailedParticipantListSave)
            .then(showSuccess)
            .catch(showRequestError)
            .finally(() => setDeleteDialogOpen(false));
    };

    const handleAddParticipant = (email: string, roleId: number) => {
        const trimmed = email.trim();
        if (!EMAIL_PATTERN.test(trimmed)) {
            showError(ts('invalid_email_enter', 'Please enter a valid e-mail address.'));
            return;
        }
        if (hasParticipant(trimmed, roleId)) {
            showError(ts('pl_user_role_exists', 'The user already exists in this list with the given role.'));
            return;
        }
        addParticipantToList({ list_id: list.id, email: trimmed, role_id: roleId })
            .unwrap()
            .then(rejectFailedParticipantListSave)
            .then(response => {
                showSuccess(response);
                onCloseEditor();
            })
            .catch(showRequestError);
    };

    const handleRoleChange = (participant: ParticipantListParticipant, roleId: number) => {
        if (hasParticipant(participant.email, roleId)) {
            showError(ts('pl_user_role_exists', 'The user already exists in this list with the given role.'));
            return;
        }
        setBusyParticipantId(participant.id);
        updateParticipantInList({ participant_id: participant.id, role_id: roleId })
            .unwrap()
            .then(rejectFailedParticipantListSave)
            .then(showSuccess)
            .catch(showRequestError)
            .finally(() => setBusyParticipantId(null));
    };

    const handleRemoveParticipant = () => {
        if (removedParticipant == null) {
            return;
        }
        deleteParticipantFromList({ participant_id: removedParticipant.id })
            .unwrap()
            .then(rejectFailedParticipantListSave)
            .then(showSuccess)
            .catch(showRequestError)
            .finally(() => setRemovedParticipant(null));
    };

    return (
        <Paper variant="outlined" sx={{ paddingX: 2, paddingY: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 40 }}>
                {isRenaming ? (
                    <ParticipantListNameInput
                        initialValue={list.name}
                        disabled={isRenameLoading}
                        confirmTooltip={ts('plt_confirm', 'Confirm')}
                        cancelTooltip={ts('plt_cancel', 'Cancel')}
                        onConfirm={handleRename}
                        onCancel={onCloseEditor}
                    />
                ) : (
                    <>
                        <Typography variant="subtitle1" noWrap sx={{ flex: 1, minWidth: 0 }}>{list.name}</Typography>
                        <Tooltip title={isOpen ? ts('plt_collapse_list', 'Collapse list') : ts('plt_expand_list', 'Expand list')}>
                            <IconButton size="small" onClick={onToggle}>
                                {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={ts('plt_mod_list_name', "Edit list's name")}>
                            <IconButton size="small" onClick={onStartRename}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={ts('plt_del_list', 'Delete list')}>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    onCloseEditor();
                                    setDeleteDialogOpen(true);
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
            </Stack>
            <Collapse in={isOpen} unmountOnExit>
                <Stack spacing={1} sx={{ paddingY: 1 }}>
                    {list.participants.map(participant => (
                        <ParticipantRow
                            key={participant.id}
                            participant={participant}
                            roles={roles}
                            disabled={busyParticipantId === participant.id}
                            onRoleChange={(roleId) => handleRoleChange(participant, roleId)}
                            onRemove={() => {
                                onCloseEditor();
                                setRemovedParticipant(participant);
                            }}
                        />
                    ))}
                    {isAddingParticipant ? (
                        <NewParticipantRow
                            roles={roles}
                            disabled={isAddLoading}
                            onConfirm={handleAddParticipant}
                            onCancel={onCloseEditor}
                        />
                    ) : (
                        <Stack direction="row" justifyContent="flex-end">
                            <Tooltip title={ts('plt_add_par_to_list', 'Add participant to this list')} placement="left">
                                <IconButton size="small" color="primary" onClick={onStartAddParticipant}>
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
            </Collapse>
            <DeleteUserDialog
                open={isDeleteDialogOpen}
                disabled={isDeleteListLoading}
                message={ts('pl_del_list_alert', 'Confirm deleting list by clicking OK button below.')}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteList}
            />
            <DeleteUserDialog
                open={removedParticipant != null}
                disabled={isRemoveLoading}
                message={ts('pl_del_partip_alert', 'Confirm deleting participant by clicking OK button below.')}
                onClose={() => setRemovedParticipant(null)}
                onConfirm={handleRemoveParticipant}
            />
        </Paper>
    );
};
